import { useEffect, useState } from "react";
import { Globe, Lock, Save, Loader2, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * PublicProfileSection — opt-in toggle that publishes a *minimal* subset of
 * a vessel (name, type, photo, bio, hailing port) so other Bosun's Mate
 * users can find and link it from their Cruiser Contacts.
 *
 * Privacy posture:
 *   - Default OFF.
 *   - The captain sees exactly what's shared before they toggle.
 *   - Opt-out is instant — the vessel disappears from search immediately.
 *   - We NEVER share logs, financials, engine data, or location.
 *
 * Self-contained: does its own load + save so it doesn't have to thread
 * through the giant VesselSettings save flow.
 */
export default function PublicProfileSection({ vesselId }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [flash, setFlash] = useState(false);

    const [enabled, setEnabled] = useState(false);
    const [bio, setBio] = useState("");
    const [hailingPort, setHailingPort] = useState("");
    const [vesselName, setVesselName] = useState("");
    const [heroPhoto, setHeroPhoto] = useState(null);

    useEffect(() => {
        if (!vesselId) return;
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(`/vessels/${vesselId}`);
                if (!alive) return;
                setEnabled(!!data.public_profile);
                setBio(data.public_bio || "");
                setHailingPort(data.public_hailing_port || "");
                setVesselName(data.name || "");
                setHeroPhoto(data.hero_photo_url || null);
            } catch (_e) {
                // Non-fatal — section just stays in its empty state.
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [vesselId]);

    const save = async () => {
        setErr("");
        setSaving(true);
        try {
            const { data } = await api.patch(`/vessels/${vesselId}`, {
                public_profile: enabled,
                public_bio: bio.trim() || null,
                public_hailing_port: hailingPort.trim() || null,
            });
            setEnabled(!!data.public_profile);
            setFlash(true);
            setTimeout(() => setFlash(false), 1800);
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message || "Couldn't save. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return null;

    return (
        <div
            data-testid="public-profile-section"
            className="bm-card"
            style={{ padding: 22, marginBottom: 22, border: "1px solid var(--bm-border-soft)" }}
        >
            <div style={{
                display: "flex", alignItems: "flex-start", gap: 14,
                marginBottom: enabled ? 18 : 0,
            }}>
                <div style={{ flex: 1 }}>
                    <div style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
                        color: "var(--bm-gold)", marginBottom: 6,
                    }}>
                        {enabled ? <Globe size={11} /> : <Lock size={11} />}
                        {enabled ? "Public profile" : "Private vessel"}
                    </div>
                    <h3 className="bm-serif" style={{ margin: 0, fontSize: 18, fontWeight: 500 }}>
                        {enabled ? "This boat is discoverable" : "Make this boat discoverable"}
                    </h3>
                    <p style={{ margin: "8px 0 0", fontSize: 13, color: "var(--bm-text-muted)", lineHeight: 1.55 }}>
                        {enabled ? (
                            <>Other Bosun&apos;s Mate captains can find <strong>{vesselName}</strong> when searching by boat name from their Cruiser Contacts. They see only what&apos;s below — never your logs, financials, or location.</>
                        ) : (
                            "Opt in to let other Bosun's Mate captains link to your boat from their Cruiser Contacts. We'll only share your boat name, type, photo, bio, and hailing port — nothing else."
                        )}
                    </p>
                </div>
                <label
                    data-testid="public-profile-toggle"
                    style={{
                        display: "inline-flex", alignItems: "center", gap: 8,
                        cursor: "pointer", userSelect: "none",
                        padding: "6px 10px",
                        borderRadius: 999,
                        border: `1px solid ${enabled ? "var(--bm-gold)" : "var(--bm-border-soft)"}`,
                        background: enabled ? "rgba(200, 162, 92, 0.12)" : "transparent",
                        fontSize: 12, fontWeight: 500,
                        color: enabled ? "var(--bm-gold)" : "var(--bm-text-muted)",
                        whiteSpace: "nowrap",
                    }}
                >
                    <input
                        data-testid="public-profile-toggle-input"
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) => setEnabled(e.target.checked)}
                        style={{ accentColor: "var(--bm-gold)", margin: 0 }}
                    />
                    {enabled ? "ON" : "OFF"}
                </label>
            </div>

            {enabled && (
                <div data-testid="public-profile-fields" style={{
                    display: "grid", gap: 12, gridTemplateColumns: "1fr",
                }}>
                    <div>
                        <label className="bm-label">Short bio (1-2 sentences)</label>
                        <textarea
                            data-testid="public-profile-bio"
                            className="bm-input"
                            rows={2}
                            value={bio}
                            maxLength={300}
                            onChange={(e) => setBio(e.target.value)}
                            placeholder={`A line about ${vesselName || "your boat"} — where you sail, what you love.`}
                            style={{ resize: "vertical" }}
                        />
                        <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--bm-text-dim)", textAlign: "right" }}>
                            {bio.length}/300
                        </p>
                    </div>
                    <div>
                        <label className="bm-label">Hailing port (optional)</label>
                        <input
                            data-testid="public-profile-hailing-port"
                            className="bm-input"
                            value={hailingPort}
                            maxLength={120}
                            onChange={(e) => setHailingPort(e.target.value)}
                            placeholder="Sidney, BC"
                        />
                    </div>

                    {/* Preview of what other captains will see */}
                    <div style={{
                        padding: "12px 14px",
                        borderRadius: 8,
                        background: "rgba(255, 255, 255, 0.03)",
                        border: "1px dashed var(--bm-border-soft)",
                    }}>
                        <div style={{
                            fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase",
                            color: "var(--bm-text-dim)", marginBottom: 8,
                        }}>What others will see</div>
                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            {heroPhoto && (
                                <img
                                    src={heroPhoto}
                                    alt=""
                                    style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6, border: "1px solid var(--bm-border-soft)" }}
                                />
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="bm-serif" style={{ fontSize: 15, fontWeight: 500 }}>{vesselName}</div>
                                {bio && <p style={{ margin: "3px 0 0", fontSize: 12, color: "var(--bm-text-muted)" }}>{bio}</p>}
                                {hailingPort && <p style={{ margin: "3px 0 0", fontSize: 11, color: "var(--bm-text-dim)" }}>📍 {hailingPort}</p>}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div style={{
                display: "flex", justifyContent: "flex-end", gap: 10,
                marginTop: enabled ? 14 : 14, paddingTop: enabled ? 12 : 12,
                borderTop: "1px solid var(--bm-border-soft)",
                alignItems: "center",
            }}>
                {flash && (
                    <span style={{ fontSize: 12, color: "var(--bm-gold)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Check size={12} /> Saved
                    </span>
                )}
                {err && (
                    <span data-testid="public-profile-error" style={{ fontSize: 12, color: "#e76f51", flex: 1 }}>{err}</span>
                )}
                <button
                    data-testid="public-profile-save-btn"
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="bm-btn bm-btn-gold"
                    style={{ padding: "9px 16px", fontSize: 12, opacity: saving ? 0.6 : 1 }}
                >
                    {saving ? <Loader2 size={12} className="bm-spin" /> : <Save size={12} />}
                    {saving ? "Saving…" : enabled ? "Save profile" : "Confirm settings"}
                </button>
            </div>
        </div>
    );
}
